import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getModeHeights } from '../../constants/agGridStyles';

/**
 * TableSkeleton - Skeleton placeholder for AG Grid tables
 *
 * Shows a realistic table skeleton with header row and data rows.
 * When `mode` is provided, uses real AG Grid heights from agGridStyles.
 * Supports responsive height objects (e.g. { xs: '50vh', lg: '75vh' }).
 *
 * @param {number} rows - Number of data rows to display (default: 8)
 * @param {number} columns - Number of columns to display (default: 6)
 * @param {number|object} height - Height (number, string, or MUI responsive object)
 * @param {boolean} showHeader - Whether to show header row (default: true)
 * @param {string} contentWidthFirst - Width of first column content (default: '85%')
 * @param {string} contentWidthOther - Width of other column content (default: '60%')
 * @param {string} mode - AG Grid mode: 'compsheet' | 'financial' | 'index' | 'simple' (optional)
 * @param {boolean} showPinnedRows - Show 2 pinned bottom rows for Mean/Median (default: false)
 */
const TableSkeleton = ({
  rows = 8,
  columns = 6,
  height,
  showHeader = true,
  contentWidthFirst = '85%',
  tableWidth = '70%',
  maxWidth,
  contentWidthOther = '60%',
  mode,
  showPinnedRows = false,
}) => {
  const theme = useTheme();
  const tableBackground =
    theme.table?.header || theme.palette.background.paper;
  const alternateRowBackground =
    theme.tableHeader?.highlight || theme.palette.action.hover;

  // Use mode-specific heights when mode is provided, otherwise defaults
  const modeHeights = mode ? getModeHeights(mode) : null;
  const rowHeight = modeHeights ? modeHeights.rowHeight : 36;
  const headerHeight = modeHeights ? modeHeights.headerHeight : 40;
  const pinnedRowHeight = modeHeights?.pinnedRowHeight || rowHeight + 4;

  // When height is a responsive object or vh string, use flex rows instead of fixed px
  const isFlexHeight = height && (typeof height === 'object' || (typeof height === 'string' && height.includes('vh')));
  const pinnedRowCount = showPinnedRows ? 2 : 0;
  const calculatedHeight = height || (showHeader ? headerHeight : 0) + rows * rowHeight + pinnedRowCount * pinnedRowHeight + 8;

  // Vary column widths for a more realistic look
  const getColumnWidth = (index) => {
    if (index === 0) return '18%'; // First column (usually name/ticker) wider
    return `${82 / (columns - 1)}%`;
  };

  return (
    <Box
      data-testid="table-skeleton"
      sx={{
        width: tableWidth,
        ...(maxWidth && { maxWidth }),
        height: calculatedHeight,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: tableBackground,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ...(isFlexHeight ? { flex: '0 0 auto', minHeight: headerHeight } : { height: headerHeight }),
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: tableBackground,
          }}
        >
          {[...Array(columns)].map((_, i) => (
            <Box key={i} sx={{ width: getColumnWidth(i), pr: 1 }}>
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          ))}
        </Box>
      )}

      {/* Data rows */}
      {[...Array(rows)].map((_, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: 'flex',
            alignItems: 'center',
            ...(isFlexHeight ? { flex: 1, minHeight: 0 } : { height: rowHeight }),
            px: 2,
            borderBottom: rowIndex < rows - 1 || showPinnedRows ? 1 : 0,
            borderColor: 'divider',
            bgcolor:
              rowIndex % 2 === 0
                ? tableBackground
                : alternateRowBackground,
            opacity: Math.max(0.4, 1 - rowIndex * 0.015),
          }}
        >
          {[...Array(columns)].map((_, colIndex) => (
            <Box key={colIndex} sx={{ width: getColumnWidth(colIndex), pr: 1 }}>
              <Skeleton
                variant="text"
                width={colIndex === 0 ? contentWidthFirst : contentWidthOther}
                height={18}
              />
            </Box>
          ))}
        </Box>
      ))}

      {/* Pinned bottom rows (Mean/Median) */}
      {showPinnedRows && ['Mean', 'Median'].map((label, pinnedIndex) => (
        <Box
          key={label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            ...(isFlexHeight ? { flex: '0 0 auto', minHeight: pinnedRowHeight } : { height: pinnedRowHeight }),
            px: 2,
            borderBottom: pinnedIndex === 0 ? 1 : 0,
            borderColor: 'divider',
            bgcolor: tableBackground,
          }}
        >
          {[...Array(columns)].map((_, colIndex) => (
            <Box key={colIndex} sx={{ width: getColumnWidth(colIndex), pr: 1 }}>
              <Skeleton
                variant="text"
                width={colIndex === 0 ? '50%' : contentWidthOther}
                height={18}
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default TableSkeleton;
